import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/Auth/authContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { deviceTransferService } from '../../services/deviceTransferService';
import { isAbortError } from '../../utils/abortError';
import { BellIcon } from './icons';

const POLL_MS = 60_000;

/**
 * The bell in the top bar: how many take-over requests wait for this
 * person's answer: client administrators answer their own, ANEP managers
 * watch every pending request since any may end up with them. Refreshed
 * every minute and when the tab regains focus.
 */
export const TransferBell: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { isClientAdmin, isAnepManager } = useClientAccess();
  const watches = isClientAdmin || isAnepManager;
  const { t } = useI18n();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !watches) return;
    const ac = new AbortController();
    const load = () => {
      deviceTransferService.inboxCount(ac.signal)
        .then(setPending)
        .catch((err) => { if (!isAbortError(err)) console.warn('[PORTABILITY] inbox count failed:', err?.message || err); });
    };
    load();
    const timer = setInterval(load, POLL_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      ac.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuthenticated, watches]);

  if (!isAuthenticated || !watches) return null;

  const label = pending > 0 ? t('portability.bell.pending', { count: pending }) : t('portability.bell.none');
  return (
    <button type="button" className="topbar-icon-btn transfer-bell" data-testid="btn-transfer-bell" onClick={() => navigate('/portability')} title={label} aria-label={label}>
      <BellIcon />
      {pending > 0 && <span className="transfer-bell-badge" data-testid="transfer-bell-count">{pending > 99 ? '99+' : pending}</span>}
    </button>
  );
};
